// Invoice service — invoice-specific business logic and orchestration.
// Composes InvoiceDao for data access. No raw Supabase calls here.

import { InvoiceDao } from './InvoiceDao.ts';
import { FakturowniaEdgeFnClient } from '../edgeFn/FakturowniaEdgeFnClient.ts';
import { applyCorrections } from './effectiveLineItems.ts';
import {
  computeLineMismatches as pureLineMismatches,
  computeTotalsMismatch as pureTotalsMismatch,
  computeReconcileKpis as pureReconcileKpis,
  type LineMismatch,
  type TotalsMismatch,
  type ReconcileKpis,
  type ReconcileKpiInvoice,
} from './reconcileMath.ts';
import type { SupabaseClient, InvalidateFn, MutationOptions } from '../base.ts';
import type {
  InvoiceRow,
  InvoiceLineRow,
  InvoiceLinkRow,
  InvoiceInsert,
  InvoiceLineInsert,
  InvoiceLinkInsert,
  InvoiceSummary,
  Invoice,
  InvoiceLineItem,
  InvoiceLink,
} from '@shared/types/invoice.ts';
import type { Pricing, DisplayPricing, PricingSide } from '../../types.ts';
import { round2, moneyDiff } from '../../calc.ts';

// ── Result types ────────────────────────────────────────────────────────

export interface CreateInvoiceResult {
  invoice: InvoiceRow;
  lineItems: InvoiceLineRow[];
}

export interface CreateInvoiceWithLinksResult extends CreateInvoiceResult {
  links: InvoiceLinkRow[];
}

// ── InvoiceService ──────────────────────────────────────────────────────

export class InvoiceService {
  readonly dao: InvoiceDao;
  readonly fakturownia: FakturowniaEdgeFnClient;

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    this.dao = new InvoiceDao(sb, invalidate);
    this.fakturownia = new FakturowniaEdgeFnClient(sb);
  }

  /** Create an invoice with its line items. Returns the created invoice and line items with IDs. */
  async createInvoiceWithLineItems(
    invoice: InvoiceInsert,
    lineItems: Omit<InvoiceLineInsert, 'invoice_id'>[],
  ): Promise<CreateInvoiceResult> {
    const created = await this.dao.create(invoice, { invalidate: false });
    const lineItemsWithInvoiceId = lineItems.map((li, i) => ({
      ...li,
      invoice_id: created.id,
      ordinal: li.ordinal ?? i + 1,
    }));
    const createdLineItems = await this.dao.createLineItems(lineItemsWithInvoiceId);
    return { invoice: created, lineItems: createdLineItems };
  }

  /** Create an invoice with line items and transaction links (1:1 ordinal mapping). */
  async createInvoiceWithLineItemsAndLinks(
    invoice: InvoiceInsert,
    lineItems: Omit<InvoiceLineInsert, 'invoice_id'>[],
    transactionIds: number[],
    side: PricingSide,
  ): Promise<CreateInvoiceWithLinksResult> {
    if (transactionIds.length !== lineItems.length) {
      throw new Error(`transactionIds length (${transactionIds.length}) must match lineItems length (${lineItems.length})`);
    }
    const { invoice: created, lineItems: createdLineItems } = await this.createInvoiceWithLineItems(invoice, lineItems);

    const links: InvoiceLinkInsert[] = transactionIds.map((txId, i) => ({
      invoice_id: created.id,
      transaction_id: txId,
      transaction_transport_id: null,
      side,
      invoice_line_item_id: createdLineItems[i]?.id ?? null,
      stale: false,
      // Links created alongside an operator-generated invoice are manual provenance:
      // the reconcile sweep (deletes only source='auto') must not erase them.
      source: 'manual',
    }));
    const createdLinks = await this.dao.createLinks(links);
    return { invoice: created, lineItems: createdLineItems, links: createdLinks };
  }

  /** Get full invoice detail — one call, everything pre-computed for rendering. */
  async getInvoiceDetail(invoiceId: number): Promise<Invoice> {
    const [invoice, bankAccounts] = await Promise.all([
      this.dao.getWithCompanies(invoiceId),
      this.dao.getBankAccounts(invoiceId),
    ]);

    const rawCorrections = invoice.corrections?.filter(c => !c.is_deleted) ?? [];
    const correctionIds = rawCorrections.map(c => c.id);

    // Parallel: line items + resolved links + correction line items
    const [lineItems, resolvedLinks, corrLineItemsMap] = await Promise.all([
      this.dao.getLineItems(invoiceId),
      this.dao.getResolvedLinks(invoiceId),
      correctionIds.length > 0 ? this.dao.getLineItemsForInvoices(correctionIds) : Promise.resolve(new Map<number, InvoiceLineRow[]>()),
    ]);

    const corrLineItems: InvoiceLineRow[] = [];
    for (const items of corrLineItemsMap.values()) corrLineItems.push(...items);

    // Group links by line item
    const linksByLi = new Map<number, InvoiceLink[]>();
    for (const link of resolvedLinks) {
      if (link.invoice_line_item_id == null) continue;
      const bucket = linksByLi.get(link.invoice_line_item_id);
      if (bucket) bucket.push(link);
      else linksByLi.set(link.invoice_line_item_id, [link]);
    }

    const currency = invoice.currency || 'PLN';

    // Build display line items using applyCorrections for correction data
    const effective = applyCorrections(lineItems, corrLineItems);
    const effectiveByOrdinal = new Map(effective.map(e => [e.ordinal, e]));

    const displayLineItems: InvoiceLineItem[] = lineItems.map(li => {
      const links = linksByLi.get(li.id) ?? [];
      const eff = effectiveByOrdinal.get(li.ordinal);
      const pricing: DisplayPricing = eff ?? { ...li };

      return {
        id: li.id,
        ordinal: li.ordinal,
        name: li.name,
        description: li.description,
        pricing,
        links,
      };
    });

    // Build corrections display
    const corrections: InvoiceSummary[] = rawCorrections.map(c => ({
      id: c.id,
      invoice_number: c.invoice_number,
      invoice_type: c.invoice_type,
      direction: invoice.direction,
      currency: c.currency,
      review_status: 'approved' as InvoiceRow['review_status'],
      invoice_date: c.issue_date,
      net_amount: c.net_amount,
      vat_amount: c.vat_amount,
      gross_amount: c.gross_amount,
    }));

    // Build totals
    const totals = buildTotals(invoice, displayLineItems, currency);

    return { invoice, lineItems: displayLineItems, corrections, bankAccounts, totals };
  }

  /** Replace all links for an invoice (delete old, insert new). */
  async replaceLinks(invoiceId: number, newLinks: InvoiceLinkInsert[], opts?: MutationOptions): Promise<InvoiceLinkRow[]> {
    await this.dao.deleteLinks(invoiceId, { invalidate: false });
    return this.dao.createLinks(newLinks, opts);
  }

  /** Get line items with their resolved links, diffs, and correction data. Ready for table rendering.
   *  Pass correctionInvoiceIds to compute before/delta/after for each line item. */
  async getLineItemsWithLinks(invoiceId: number, correctionInvoiceIds?: number[]): Promise<LineItemWithLinks[]> {
    const [lineItems, resolvedLinks] = await Promise.all([
      this.dao.getLineItems(invoiceId),
      this.dao.getResolvedLinks(invoiceId),
    ]);

    // Fetch correction line items if needed
    const correctionLineItems: InvoiceLineRow[] = [];
    if (correctionInvoiceIds && correctionInvoiceIds.length > 0) {
      const corrMap = await this.dao.getLineItemsForInvoices(correctionInvoiceIds);
      for (const items of corrMap.values()) correctionLineItems.push(...items);
    }

    const linksByLi = new Map<number, InvoiceLink[]>();
    for (const link of resolvedLinks) {
      if (link.invoice_line_item_id == null) continue;
      const bucket = linksByLi.get(link.invoice_line_item_id);
      if (bucket) bucket.push(link);
      else linksByLi.set(link.invoice_line_item_id, [link]);
    }

    // Merge corrections into DisplayPricing
    const effective = correctionLineItems.length > 0
      ? applyCorrections(lineItems, correctionLineItems)
      : null;
    const effectiveByOrdinal = effective ? new Map(effective.map(e => [e.ordinal, e])) : null;

    return lineItems.map(li => {
      const links = linksByLi.get(li.id) ?? [];
      const diff = links.length > 0 ? computeDiff(li, links) : null;
      const eff = effectiveByOrdinal?.get(li.ordinal);
      const correction: DisplayPricing | null = eff?.delta ? eff : null;
      return { lineItem: li, links, diff, correction };
    });
  }

  // ── Reconcile derivations (pure — delegates to reconcileMath) ──────────
  // These are sync wrappers around reconcileMath so the FE form helpers go
  // through the service surface instead of redoing the math at the call
  // site. Backend code paths (edge fn / Cloud Run) call the same pure
  // helpers; the service method exists for parity at the FE boundary.

  /** Map of line-item id → mismatch detail. Lines with no links or with
   *  links that sum to the line totals are omitted. */
  computeLineMismatches(lineItems: InvoiceLineItem[]): Map<number, LineMismatch> {
    return pureLineMismatches(lineItems);
  }

  /** Totals-level mismatch + count of non-zero unlinked lines. */
  computeTotalsMismatch(
    lineItems: InvoiceLineItem[],
    totalsNet: number,
    totalsGross: number,
  ): TotalsMismatch {
    return pureTotalsMismatch(lineItems, totalsNet, totalsGross);
  }

  /** KPI band derivation — matched/total/reconcile% + paid%/remaining. */
  computeReconcileKpis(invoice: ReconcileKpiInvoice, lineItems: InvoiceLineItem[]): ReconcileKpis {
    return pureReconcileKpis(invoice, lineItems);
  }
}

// ── Types ───────────────────────────────────────────────────────────────

export interface LineItemDiff extends Pricing {
  vatRateMatch: boolean;
}

export interface LineItemWithLinks {
  lineItem: InvoiceLineRow;
  links: InvoiceLink[];
  diff: LineItemDiff | null;
  correction: DisplayPricing | null;
}

// ── Helpers ─────────────────────────────────────────────────────────────

function computeDiff(li: InvoiceLineRow, links: InvoiceLink[]): LineItemDiff {
  let sumQty = 0, sumNet = 0, sumVat = 0, sumGross = 0;
  for (const l of links) {
    sumQty += l.pricing.quantity;
    sumNet += l.pricing.net_amount;
    sumVat += l.pricing.vat_amount;
    sumGross += l.pricing.gross_amount;
  }
  const priceMismatch = links.some(l => moneyDiff(l.pricing.unit_price, li.unit_price) !== 0);
  const liVat = li.vat_rate_code.toLowerCase();
  const vatRateMatch = links.every(l => l.pricing.vat_rate_code.toLowerCase() === liVat);
  return {
    quantity: moneyDiff(sumQty, li.quantity),
    unit: li.unit,
    unit_price: priceMismatch ? moneyDiff(links[0]?.pricing.unit_price ?? 0, li.unit_price) : 0,
    currency: li.currency,
    price_type: li.price_type,
    vat_rate_code: li.vat_rate_code,
    net_amount: moneyDiff(sumNet, li.net_amount),
    vat_amount: moneyDiff(sumVat, li.vat_amount),
    gross_amount: moneyDiff(sumGross, li.gross_amount),
    vatRateMatch,
  };
}

function buildTotals(invoice: InvoiceRow, lineItems: InvoiceLineItem[], currency: string): DisplayPricing {
  const base: DisplayPricing = {
    quantity: 0, unit: '', unit_price: 0,
    currency, price_type: 'per_ton', vat_rate_code: '23',
    net_amount: invoice.net_amount ?? 0, vat_amount: invoice.vat_amount ?? 0, gross_amount: invoice.gross_amount ?? 0,
  };

  const hasCorr = lineItems.some(li => !!li.pricing.delta);
  if (!hasCorr) return base;

  let dNet = 0, dVat = 0, dGross = 0;
  for (const li of lineItems) {
    if (li.pricing.delta?.net_amount) dNet += li.pricing.delta.net_amount;
    if (li.pricing.delta?.vat_amount) dVat += li.pricing.delta.vat_amount;
    if (li.pricing.delta?.gross_amount) dGross += li.pricing.delta.gross_amount;
  }

  return {
    ...base,
    net_amount: round2((invoice.net_amount ?? 0) + dNet),
    vat_amount: round2((invoice.vat_amount ?? 0) + dVat),
    gross_amount: round2((invoice.gross_amount ?? 0) + dGross),
    original: { ...base },
    delta: {
      quantity: 0, unit: '', unit_price: 0,
      currency, price_type: 'per_ton', vat_rate_code: '23',
      net_amount: round2(dNet), vat_amount: round2(dVat), gross_amount: round2(dGross),
    },
  };
}
